import { useMemo, useState } from "react";
import {
  getCurrentCostBearerScreenStep,
  getCurrentPracticePatientFormQuestionsSortedArray,
  getCurrentQuestionIndex,
  getDidUserCompleteCurrentQuestion,
  getUserAnswerToQuestion,
} from "../../../core/store/currentFormSlice";
import { useMainStore } from "../../../core/store/mainStore";
import {
  calculateCurrentQuestionIndexInForm,
  calculateTotalQuestionsInForm,
  getNextButtonDisabled,
  getNextButtonText,
  getQuestionTypeChecks,
} from "./formStateUtils";
import { useFormNavigation } from "./useFormNavigation";

export const useHandleFormScreens = () => {
  const formQuestions = useMainStore(
    getCurrentPracticePatientFormQuestionsSortedArray,
  );
  const [nextButtonLoading, setNextButtonLoading] = useState(false);
  const currentQuestionIndex = useMainStore(getCurrentQuestionIndex);
  const setCurrentQuestionIndex = useMainStore((state) => state.setCurrentQuestionIndex);
  const currentQuestion = formQuestions?.[currentQuestionIndex];
  const userCompletedCurrentQuestion = useMainStore((state) =>
    getDidUserCompleteCurrentQuestion(state, currentQuestion?.id || ""),
  );
  const userAnswer = useMainStore((state) =>
    getUserAnswerToQuestion(state, currentQuestion?.answer?.answerId || ""),
  );

  // Get question type checks
  const {
    currentQuestionIsContract,
    currentQuestionIsCostBearer,
    questionIsConditionAcceptance,
    questionIsPersonalInfo,
    questionIsContract,
    questionIsDocumentUpload,
    questionIsRecipeUpload,
    isQuestionOptional,
  } = getQuestionTypeChecks(currentQuestion);

  const currentCostBearerScreenStep =
    useMainStore(getCurrentCostBearerScreenStep) ?? 0;
  const amountTotalQuestions = formQuestions?.length ?? 0;

  // Calculate form progress
  const amountTotalQuestionsInForm = calculateTotalQuestionsInForm(
    formQuestions || [],
  );

  const currentQuestionIndexInForm = useMemo(
    () =>
      calculateCurrentQuestionIndexInForm(
        formQuestions || [],
        currentQuestionIndex,
        currentQuestionIsCostBearer,
        currentCostBearerScreenStep,
      ),
    [
      formQuestions,
      currentQuestionIndex,
      currentQuestionIsCostBearer,
      currentCostBearerScreenStep,
    ],
  );

  // Calculate button states
  const nextButtonDisabled = getNextButtonDisabled(
    userCompletedCurrentQuestion,
    questionIsConditionAcceptance,
  );

  const nextButtonText = getNextButtonText(
    currentQuestionIsCostBearer,
    currentCostBearerScreenStep,
    currentQuestionIsContract,
    currentQuestionIndexInForm,
    amountTotalQuestionsInForm,
    questionIsConditionAcceptance,
  );

  // Form navigation hooks
  const { handleNextQuestion, handleSkipQuestion, handlePreviousQuestion, hideBackButton } =
    useFormNavigation({
      currentQuestion,
      currentQuestionIndex,
      setCurrentQuestionIndex,
      currentQuestionIsCostBearer,
      questionIsConditionAcceptance,
      questionIsPersonalInfo,
      questionIsContract,
      questionIsDocumentUpload,
      questionIsRecipeUpload,
      userAnswer,
      nextButtonDisabled,
      setNextButtonLoading,
      amountTotalQuestions,
    });

  return {
    currentQuestion,
    currentQuestionIndexInForm,
    amountTotalQuestionsInForm,
    handleNextQuestion,
    nextButtonDisabled,
    nextButtonText,
    nextButtonLoading,
    isQuestionOptional,
    handlePreviousQuestion,
    hideBackButton,
    handleSkipQuestion,
    currentQuestionIsCostBearer,
  };
};
